import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import {
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon,
  LoaderIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@/components/icons';
import './agent-form.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import MaasModelSelect from '@/components/MaasModelSelect';
import {
  PLUGIN_SUB_TYPE_CLASS,
  PLUGIN_SUB_TYPE_INSTANCE,
  PLUGIN_SUB_TYPE_TOOLKIT,
  PLUGIN_SUB_TYPE_USER_TOOLKIT,
  PLUGIN_SUB_TYPE_APPLICATION,
  PLUGIN_TYPE_SANDBOX,
  PLUGIN_TYPE_SKILLGROUP,
  PLUGIN_TYPE_RAG,
  PLUGIN_TYPE_MEMORY,
  PLUGIN_TYPE_MCP,
} from '@/constants/plugin';
import { infiniMemoryApi } from '@/api/infiniMemory.api';
import { isInfiniMemoryVisible } from '@/config';
import { ALL_PLUGIN_OPTIONS } from '@/constants/pluginNavMapping';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  ChannelType,
  ChannelAccountBindingMode,
  ToolPermissionDefaultAction,
} from '@/proto/agentplatform/v1/channel_pb';
import type { MaasModel } from '@/proto/agentplatform/v2/maas_pb';
import { maasApi } from '@/api/maas.api';
import { groupModels } from '@/utils/model-display';
import {
  SECTIONS,
  type AgentFormProps,
  useAgentFormData,
  CHANNEL_PARAM_DEFINITIONS,
  FILESYSTEM_TOOL_GROUPS,
  FILESYSTEM_ALL_TOOLS,
  FILESYSTEM_READONLY_TOOLS,
} from './agentFormTypes';
import ChannelParamInput from './ChannelParamInput';
import FaultToleranceFields from './FaultToleranceFields';

function findMaasModelByName(
  resp: { items?: readonly MaasModel[] } | undefined,
  modelName: string
): MaasModel | undefined {
  if (!resp?.items || !modelName) return undefined;
  return resp.items.find((m) => m.name === modelName);
}

function IconPlug({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function IconPlugin({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 00-1 1v1a2 2 0 11-4 0v-1a1 1 0 00-1-1h-3a1 1 0 00-1 1v-3a1 1 0 00-1.293-.707L3 13a1 1 0 00-1 1v-3a1 1 0 011-1h1a2 2 0 100-4H5a1 1 0 00-1-1V6a1 1 0 011-1h3a1 1 0 001-1V4z"
      />
    </svg>
  );
}

function JsonInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  const [jsonError, setJsonError] = useState<string | null>(null);
  const lineCount = (value || '').split('\n').length;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange(val);
    if (!val.trim()) {
      setJsonError(null);
      return;
    }
    try {
      JSON.parse(val);
      setJsonError(null);
    } catch (err) {
      setJsonError((err as Error).message);
    }
  };

  const handleFormat = () => {
    if (!value.trim()) return;
    try {
      const parsed = JSON.parse(value);
      onChange(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch {
      // keep current value
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">默认值</Label>
          <span className="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 rounded bg-muted">
            JSON
          </span>
        </div>
        <button
          type="button"
          onClick={handleFormat}
          disabled={!!jsonError || !value.trim()}
          className="text-xs text-primary hover:text-primary/80 disabled:text-muted-foreground disabled:cursor-not-allowed"
        >
          格式化
        </button>
      </div>
      <div
        className={`rounded-md border overflow-hidden ${jsonError ? 'border-destructive' : 'border-input'}`}
      >
        <div className="flex bg-zinc-950">
          <div
            className="select-none text-right pr-3 pl-3 py-3 text-xs font-mono leading-[1.625rem] text-zinc-600 bg-zinc-900/50 border-r border-zinc-800"
            aria-hidden="true"
          >
            {Array.from({ length: Math.max(lineCount, 8) }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
          <textarea
            placeholder="{}"
            value={value}
            onChange={handleChange}
            rows={Math.max(lineCount, 8)}
            spellCheck={false}
            className="flex-1 bg-zinc-950 text-zinc-100 px-3 py-3 text-sm font-mono leading-[1.625rem] ring-offset-background placeholder:text-zinc-600 focus-visible:outline-none resize-none w-0"
          />
        </div>
      </div>
      {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground">格式说明</summary>
        <pre className="mt-2 p-3 rounded bg-muted font-mono text-[11px] leading-relaxed overflow-auto">{`[
  {
    "name": "工具名称",
    "description": "工具描述",
    "inputSchema": {
      "type": "object",
      "properties": { ... },
      "required": [...]
    },
    "outputSchema": {}
  }
]`}</pre>
        <p className="mt-1.5">
          数组格式，每个元素包含 <code className="px-1 py-0.5 rounded bg-muted">name</code>、
          <code className="px-1 py-0.5 rounded bg-muted">description</code>、
          <code className="px-1 py-0.5 rounded bg-muted">inputSchema</code>、
          <code className="px-1 py-0.5 rounded bg-muted">outputSchema</code> 四个字段
        </p>
      </details>
    </div>
  );
}

const CHANNEL_TYPE_OPTIONS: Array<{ value: ChannelType; label: string }> = [
  { value: ChannelType.FEISHU, label: '飞书' },
  { value: ChannelType.QQ, label: 'QQ' },
];

const BINDING_MODE_OPTIONS: Array<{
  value: ChannelAccountBindingMode;
  label: string;
  desc: string;
}> = [
  {
    value: ChannelAccountBindingMode.TEMPLATE,
    label: '模板绑定',
    desc: '渠道账号绑定模版，创建时即刻配置渠道参数，终端用户通过 /init 创建专属实例',
  },
  {
    value: ChannelAccountBindingMode.INSTANCE,
    label: '实例绑定',
    desc: '渠道参数在创建实例时由用户填写',
  },
];

function channelTypeLabel(type: ChannelType): string {
  const found = CHANNEL_TYPE_OPTIONS.find((item) => item.value === type);
  return found?.label ?? ChannelType[type] ?? '未知渠道';
}

function SectionCard({
  id,
  title,
  subtitle,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const meta = SECTIONS.find((s) => s.id === id);
  return (
    <section id={id} className="ac-sec">
      <aside className="ac-sec-aside">
        <div className="ac-sec-title-row">
          <span className="ac-sec-num">{meta?.num}</span>
          <h2 className="ac-sec-title">{title}</h2>
        </div>
        <p className="ac-sec-desc">{meta?.desc ?? subtitle}</p>
      </aside>
      <div className="ac-sec-body">{children}</div>
    </section>
  );
}

export default function AgentForm({
  form,
  set,
  handlers,
  showApiKey,
  setShowApiKey,
  credentialPicker = false,
  title,
  subtitle,
  submitLabel,
  isPending,
  agentLoading = false,
  backTo,
  canSubmit,
  onSubmit,
}: AgentFormProps) {
  const navigate = useNavigate();
  const { activeSection, scrollToSection } = useScrollSpy({
    root: '.platform-app .main',
    bar: '.ac-step-bar',
    content: '.ac-scroll',
    sections: SECTIONS,
  });
  const {
    sandboxList,
    sandboxClassesList,
    skillGroupClassesList,
    skillGroupsList,
    ragClassesList,
    ragsList,
    memoryList,
    memoryClassesList,
    toolkitList,
    applicationList,
    mcpList,
    mcpClassesList,
  } = useAgentFormData();

  // 租户级插件功能开关已下线，全部插件类型放开可选。
  const pluginTypeOptions = ALL_PLUGIN_OPTIONS;
  const firstAvailablePluginType = ALL_PLUGIN_OPTIONS[0];
  const showInfiniMemory = isInfiniMemoryVisible();
  const hasInfiniMemory =
    showInfiniMemory &&
    form.plugins.some(
      (plugin) => plugin.plugin_type === PLUGIN_TYPE_MEMORY && plugin.memoryMode === 'infini_memory'
    );
  const infiniMemoryScopeQuery = useQuery({
    queryKey: ['infini-memory', 'scope', 'agent-form'],
    queryFn: infiniMemoryApi.getScope,
    enabled: hasInfiniMemory,
  });
  const infiniMemoryStoresQuery = useQuery({
    queryKey: ['infini-memory', 'stores', infiniMemoryScopeQuery.data?.prefix, 'agent-form'],
    queryFn: () => infiniMemoryApi.listStores(infiniMemoryScopeQuery.data!.prefix),
    enabled: Boolean(hasInfiniMemory && infiniMemoryScopeQuery.data?.available),
  });
  const maasCredentialsQuery = useQuery({
    queryKey: ['maas-credentials', 'agent-form'],
    queryFn: () => maasApi.listCredentials(create(ListRequestSchema, { page: 1, pageSize: 200 })),
    enabled: credentialPicker,
  });

  const credentialRows = maasCredentialsQuery.data?.items ?? [];
  const selectedCredential = credentialRows.find((c) => c.id === form.api_key_ref);

  const modelsForCredentialQuery = useQuery({
    queryKey: ['maas-credential-models', form.api_key_ref],
    queryFn: () => maasApi.listModelsByCredential(form.api_key_ref),
    enabled: credentialPicker && Boolean(form.api_key_ref),
  });

  useEffect(() => {
    if (!credentialPicker || !form.api_key_ref || !modelsForCredentialQuery.data) {
      return;
    }
    const models = modelsForCredentialQuery.data.items ?? [];
    const names = models.map((m) => m.name.trim()).filter((s) => s.length > 0);
    if (names.length === 0) {
      return;
    }
    const cur = form.model_name.trim();
    if (cur && names.includes(cur)) {
      return;
    }
    const rows = maasCredentialsQuery.data?.items ?? [];
    const cred = rows.find((c) => c.id === form.api_key_ref);
    const dm = (cred?.defaultModel ?? '').trim();
    const pick = dm && names.includes(dm) ? dm : names[0];
    set('model_name', pick);
  }, [
    credentialPicker,
    form.api_key_ref,
    form.model_name,
    modelsForCredentialQuery.data,
    maasCredentialsQuery.data,
    set,
  ]);

  // 模型选择后自动填充 maxOutputLength / contextLength
  useEffect(() => {
    if (!credentialPicker || !form.model_name || !modelsForCredentialQuery.data) {
      return;
    }
    const modelData = findMaasModelByName(modelsForCredentialQuery.data, form.model_name);
    if (!modelData) {
      return;
    }
    const ec = form.extraConfig;
    if (
      ec.maxOutputLength === modelData.maxOutputLength &&
      ec.contextLength === modelData.contextLength
    ) {
      return;
    }
    set('extraConfig', {
      ...ec,
      maxOutputLength: modelData.maxOutputLength ?? BigInt(0),
      contextLength: modelData.contextLength ?? BigInt(0),
    });
  }, [credentialPicker, form.model_name, modelsForCredentialQuery.data, form.extraConfig, set]);

  const {
    addPlugin,
    removePlugin,
    updatePlugin,
    updatePluginParam,
    fetchPluginInfo,
    addChannel,
    removeChannel,
    updateChannel,
    getDuplicateParamNames,
  } = handlers;

  const duplicateChannelTypes = useMemo(() => {
    const counts = new Map<ChannelType, number>();
    for (const channel of form.channels) {
      if (channel.channelType === ChannelType.CHANNEL_TYPE_UNSPECIFIED) {
        continue;
      }
      counts.set(channel.channelType, (counts.get(channel.channelType) || 0) + 1);
    }
    const duplicates = new Set<ChannelType>();
    for (const [channelType, count] of counts.entries()) {
      if (count > 1) {
        duplicates.add(channelType);
      }
    }
    return duplicates;
  }, [form.channels]);
  const hasDuplicateChannels = duplicateChannelTypes.size > 0;

  const groupedModels = useMemo(() => {
    const models = modelsForCredentialQuery.data?.items ?? [];
    return groupModels(models);
  }, [modelsForCredentialQuery.data]);

  // 已配置进度：与原型一致，basic + model 为最小必填
  const completedCount = useMemo(() => {
    const flags = {
      basic: !!form.name.trim(),
      model: credentialPicker
        ? !!form.api_key_ref.trim() && !!form.model_name.trim()
        : !!form.api_key.trim() && !!form.model_name.trim(),
      filesystem: true,
      todoList: true,
      askUser: true,
      webSearch: !form.webSearchEnable || !!form.webSearchApiKey.trim(),
      currentTime: true,
      compression: true,
      faultTolerance: true,
      toolPermissions: true,
      channel: form.channels.length > 0,
      plugins: true,
    };
    return Object.values(flags).filter(Boolean).length;
  }, [form, credentialPicker]);

  const statusOk =
    !!form.name.trim() &&
    (credentialPicker
      ? !!form.api_key_ref.trim() && !!form.model_name.trim()
      : !!form.api_key.trim() && !!form.model_name.trim());

  return (
    <div className="agent-form-wrap">
      <div className="ac-step-bar">
        <button type="button" className="ac-step-back" onClick={() => navigate(backTo)}>
          <ChevronLeftIcon className="h-4 w-4" />
          返回 智能体模版管理
        </button>

        <div className="ac-steps">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`ac-step ${activeSection === s.id ? 'is-active' : ''}`}
              onClick={() => scrollToSection(s.id)}
            >
              <span className="ac-step-num">{s.num}</span>
              <span>{s.title}</span>
            </button>
          ))}

          <span className="shadow-bg"></span>
        </div>

        <div className="ac-progress-hint">
          {completedCount} / {SECTIONS.length} 已配置
        </div>
      </div>

      <div className="ac-scroll">
        <header className="ac-form-head">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </header>

        <div>
          <SectionCard id="basic" title="基本信息" subtitle="Agent 的名称和描述">
            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">
                  模版名称 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="例如: 智能客服、数据分析助手"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description" className="text-sm font-medium">
                  描述
                </Label>
                <ExpandableTextarea
                  id="description"
                  placeholder="简要描述此 Agent 的功能和用途..."
                  value={form.description}
                  onChange={(v) => set('description', v)}
                  collapsedRows={2}
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            id="model"
            title="模型配置"
            subtitle={
              credentialPicker
                ? '选择模型凭证（API Key 由凭证托管），再选择推理模型'
                : '选择语言模型并配置 API 密钥'
            }
          >
            <div className="space-y-5">
              {credentialPicker ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="api_key_ref" className="text-sm font-medium">
                      模型凭证 <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={form.api_key_ref}
                      onValueChange={(val) => {
                        set('api_key_ref', val);
                        if (!val) {
                          set('model_name', '');
                          return;
                        }
                        const row = credentialRows.find((c) => c.id === val);
                        const dm = (row?.defaultModel ?? '').trim();
                        set('model_name', dm);
                      }}
                    >
                      <SelectTrigger className="h-10" disabled={maasCredentialsQuery.isLoading}>
                        <SelectValue
                          placeholder={
                            maasCredentialsQuery.isLoading ? '加载凭证…' : '请选择模型凭证'
                          }
                        >
                          {selectedCredential && (
                            <span className="truncate">
                              {selectedCredential.name || selectedCredential.id}
                              {selectedCredential.defaultModel
                                ? `（默认模型 ${selectedCredential.defaultModel}）`
                                : ''}
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent searchable searchPlaceholder="搜索凭证...">
                        {credentialRows.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            searchKeywords={`${c.name || c.id} ${c.defaultModel || ''}`}
                          >
                            <span className="truncate">
                              {c.name || c.id}
                              {c.defaultModel ? `（默认模型 ${c.defaultModel}）` : ''}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {maasCredentialsQuery.isError && (
                      <p className="text-xs text-destructive">凭证列表加载失败</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="model_select" className="text-sm font-medium">
                      模型 <span className="text-destructive">*</span>
                    </Label>
                    {!form.api_key_ref ? (
                      <div className="h-10 w-full rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground flex items-center">
                        请先选择凭证
                      </div>
                    ) : modelsForCredentialQuery.isLoading ? (
                      <div className="h-10 w-full rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground flex items-center">
                        加载模型列表…
                      </div>
                    ) : (
                      <MaasModelSelect
                        id="model_select"
                        models={modelsForCredentialQuery.data?.items ?? []}
                        value={form.model_name}
                        onChange={(val) => set('model_name', val)}
                        triggerClassName="h-10"
                      />
                    )}
                    {form.api_key_ref && modelsForCredentialQuery.isError && (
                      <p className="text-xs text-destructive">模型列表加载失败，请检查凭证</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="model" className="text-sm font-medium">
                      模型
                    </Label>
                    <Input
                      id="model"
                      placeholder="请输入模型名称，例如：gpt-4o"
                      value={form.model_name}
                      onChange={(e) => set('model_name', e.target.value)}
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="api_key" className="text-sm font-medium">
                      API Key
                    </Label>
                    <div className="relative">
                      <Input
                        id="api_key"
                        type={showApiKey ? 'text' : 'password'}
                        placeholder="sk-..."
                        value={form.api_key}
                        onChange={(e) => set('api_key', e.target.value)}
                        className="h-10 font-mono text-xs pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey((prev) => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showApiKey ? (
                          <EyeSlashIcon className="h-4 w-4" />
                        ) : (
                          <EyeIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* 模型参数区域 */}
              <div className="pt-4 border-t border-border/40">
                <h3 className="text-sm font-medium mb-3">模型参数</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="temperature" className="text-sm font-medium">
                        温度
                      </Label>
                      <span className="text-sm font-mono text-muted-foreground tabular-nums">
                        {form.extraConfig.temperature.toFixed(1)}
                      </span>
                    </div>
                    <input
                      id="temperature"
                      type="range"
                      min={0}
                      max={2}
                      step={0.1}
                      value={form.extraConfig.temperature}
                      onChange={(e) =>
                        set('extraConfig', {
                          ...form.extraConfig,
                          temperature: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full h-2 cursor-pointer appearance-none rounded-full bg-muted accent-primary"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>精确 (0)</span>
                      <span>多样 (2)</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="enable_thinking" className="text-sm font-medium">
                      思考模式
                    </Label>
                    <Select
                      value={
                        form.extraConfig.enableThinking === undefined
                          ? 'default'
                          : form.extraConfig.enableThinking
                            ? 'enabled'
                            : 'disabled'
                      }
                      onValueChange={(value) =>
                        set('extraConfig', {
                          ...form.extraConfig,
                          enableThinking: value === 'default' ? undefined : value === 'enabled',
                        })
                      }
                    >
                      <SelectTrigger id="enable_thinking" className="h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">默认</SelectItem>
                        <SelectItem value="enabled">开启</SelectItem>
                        <SelectItem value="disabled">关闭</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reasoning_effort" className="text-sm font-medium">
                      thinking level
                    </Label>
                    <Input
                      id="reasoning_effort"
                      placeholder="例如 low, high, medium, max (留空表示默认)"
                      value={form.extraConfig.reasoningEffort || ''}
                      onChange={(e) =>
                        set('extraConfig', {
                          ...form.extraConfig,
                          reasoningEffort: e.target.value,
                        })
                      }
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      约束推理模型在生成过程中的思考级别（thinking
                      level）。具体取值取决于具体模型（如 low, high, max
                      等），留空表示使用模型默认。
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="presence_penalty" className="text-sm font-medium">
                      存在惩罚
                    </Label>
                    <Input
                      id="presence_penalty"
                      type="number"
                      step="0.1"
                      min="-2"
                      max="2"
                      placeholder="0"
                      value={form.extraConfig.presencePenalty || ''}
                      onChange={(e) =>
                        set('extraConfig', {
                          ...form.extraConfig,
                          presencePenalty: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      正值增加讨论新话题的概率，负值倾向于重复已有内容
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="frequency_penalty" className="text-sm font-medium">
                      频率惩罚
                    </Label>
                    <Input
                      id="frequency_penalty"
                      type="number"
                      step="0.1"
                      min="-2"
                      max="2"
                      placeholder="0"
                      value={form.extraConfig.frequencyPenalty || ''}
                      onChange={(e) =>
                        set('extraConfig', {
                          ...form.extraConfig,
                          frequencyPenalty: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      正值降低逐字重复的概率，负值增加重复倾向
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">最大生成长度</Label>
                    <div className="h-10 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground flex items-center">
                      {form.extraConfig.maxOutputLength
                        ? `${form.extraConfig.maxOutputLength.toString()} tokens`
                        : '选择模型后自动显示'}
                    </div>
                    <p className="text-xs text-muted-foreground">由模型自动提供，不可修改</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">最大上下文长度</Label>
                    <div className="h-10 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground flex items-center">
                      {form.extraConfig.contextLength
                        ? `${form.extraConfig.contextLength.toString()} tokens`
                        : '选择模型后自动显示'}
                    </div>
                    <p className="text-xs text-muted-foreground">由模型自动提供，不可修改</p>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="system_prompt" className="text-sm font-medium">
                    系统提示词
                  </Label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.system_prompt_configurable}
                      onChange={(e) => set('system_prompt_configurable', e.target.checked)}
                      className="rounded border-input"
                    />
                    <span className="text-xs text-muted-foreground">可配置</span>
                  </label>
                </div>
                <ExpandableTextarea
                  id="system_prompt"
                  placeholder="定义 Agent 的角色、行为和约束..."
                  value={form.system_prompt}
                  onChange={(v) => set('system_prompt', v)}
                  collapsedRows={4}
                  markdownPreview
                />
                <p className="text-xs text-muted-foreground">
                  系统提示词用于定义 Agent 的角色定位、行为规则和输出格式
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            id="filesystem"
            title="文件系统"
            subtitle="控制是否启用 filesystem middleware，供 Agent 读写工作区文件"
          >
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="filesystem_enable" className="text-sm font-medium">
                  启用文件系统
                </Label>
                <button
                  id="filesystem_enable"
                  type="button"
                  role="switch"
                  aria-checked={form.enableFilesystem}
                  onClick={() => set('enableFilesystem', !form.enableFilesystem)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.enableFilesystem ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.enableFilesystem ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                创建时默认勾选。取消勾选或不写入该字段时，middleware 中不包含 filesystem；存量 Agent
                默认不开启。
              </p>
              {form.enableFilesystem && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">启用的工具</div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => set('filesystemTools', [...FILESYSTEM_ALL_TOOLS])}
                      >
                        全选
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => set('filesystemTools', [...FILESYSTEM_READONLY_TOOLS])}
                      >
                        仅只读
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => set('filesystemTools', [])}
                      >
                        清空
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {FILESYSTEM_TOOL_GROUPS.map((group) => (
                      <div key={group.label} className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground">
                          {group.label}
                        </div>
                        {group.tools.map((tool) => {
                          const active = form.filesystemTools.includes(tool.value);
                          return (
                            <button
                              key={tool.value}
                              type="button"
                              title={tool.desc}
                              aria-pressed={active}
                              onClick={() =>
                                set(
                                  'filesystemTools',
                                  active
                                    ? form.filesystemTools.filter((t) => t !== tool.value)
                                    : [...form.filesystemTools, tool.value]
                                )
                              }
                              className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                                active
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-input bg-background text-foreground hover:bg-muted'
                              }`}
                            >
                              <span className="font-mono text-xs">{tool.label}</span>
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  active ? 'bg-primary' : 'bg-muted-foreground/30'
                                }`}
                              />
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    选择 filesystem middleware 暴露给模型的工具。默认全选。
                  </p>
                  <div className="space-y-2 border-t pt-4">
                    <div className="flex items-center gap-3">
                      <Label htmlFor="read_before_write_enable" className="text-sm font-medium">
                        写前必须读
                      </Label>
                      <button
                        id="read_before_write_enable"
                        type="button"
                        role="switch"
                        aria-checked={form.readBeforeWriteEnable}
                        onClick={() => set('readBeforeWriteEnable', !form.readBeforeWriteEnable)}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                          form.readBeforeWriteEnable ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                            form.readBeforeWriteEnable ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      read_before_write middleware。开启后对已存在文件的 write_file / edit_file
                      操作要求先读过当前版本，防止盲写覆盖；新建文件不受影响。
                    </p>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="todoList"
            title="待办列表"
            subtitle="todo_list middleware 默认不开启，启用后可附加 description 引导"
          >
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="todo_list_enable" className="text-sm font-medium">
                  启用待办列表
                </Label>
                <button
                  id="todo_list_enable"
                  type="button"
                  role="switch"
                  aria-checked={form.todoListEnable}
                  onClick={() => set('todoListEnable', !form.todoListEnable)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.todoListEnable ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.todoListEnable ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              {form.todoListEnable && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Label htmlFor="todo_list_enable_desc" className="text-sm font-medium">
                      增强描述注入
                    </Label>
                    <button
                      id="todo_list_enable_desc"
                      type="button"
                      role="switch"
                      aria-checked={form.todoListEnableDesc}
                      onClick={() => set('todoListEnableDesc', !form.todoListEnableDesc)}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        form.todoListEnableDesc ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                          form.todoListEnableDesc ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    开启后 Todo 带可选 description 字段，并引导模型调 write_todos 时一次性填好
                    content+status+description；关闭（默认）使用官方原版行为，无 description 字段。
                  </p>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="askUser"
            title="用户提问"
            subtitle="ask_user middleware 默认不开启，启用后 Agent 可向用户提问以澄清需求"
          >
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="ask_user_enable" className="text-sm font-medium">
                  启用用户提问
                </Label>
                <button
                  id="ask_user_enable"
                  type="button"
                  role="switch"
                  aria-checked={form.askUserEnable}
                  onClick={() => set('askUserEnable', !form.askUserEnable)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.askUserEnable ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.askUserEnable ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                启用后 Agent 获得 ask_user 工具，可在信息不足时暂停并等待用户回复。
              </p>
            </div>
          </SectionCard>

          <SectionCard
            id="webSearch"
            title="联网搜索"
            subtitle="web_search middleware 默认不开启，启用后 Agent 获得 web_search / web_fetch 工具"
          >
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="web_search_enable" className="text-sm font-medium">
                  启用联网搜索
                </Label>
                <button
                  id="web_search_enable"
                  type="button"
                  role="switch"
                  aria-checked={form.webSearchEnable}
                  onClick={() => set('webSearchEnable', !form.webSearchEnable)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.webSearchEnable ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.webSearchEnable ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                基于 Tavily 的联网搜索与网页抓取工具，并引导模型在回答中引用来源（citations）。 需要
                framework 的 vibe 路径支持 web_search slot（MIDDLEWARE_SLOTS），否则配置不生效。
              </p>
              {form.webSearchEnable && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="web_search_api_key" className="text-sm font-medium">
                      Tavily API Key <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="web_search_api_key"
                      type="password"
                      placeholder="tvly-..."
                      value={form.webSearchApiKey}
                      onChange={(e) => set('webSearchApiKey', e.target.value)}
                      className="h-10 font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      必填。无 key 时工具调用会直接失败（framework 无全局兜底）。
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="web_search_max_results" className="text-sm font-medium">
                      单次搜索最大结果数
                    </Label>
                    <Input
                      id="web_search_max_results"
                      type="number"
                      min={1}
                      max={20}
                      placeholder="5"
                      value={form.webSearchMaxResults || ''}
                      onChange={(e) =>
                        set('webSearchMaxResults', parseInt(e.target.value, 10) || 0)
                      }
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">留空使用 framework 默认（5）</p>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="currentTime"
            title="当前时间"
            subtitle="current_time middleware 默认不开启，启用后每次模型调用注入当前时间"
          >
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="current_time_enable" className="text-sm font-medium">
                  注入当前时间
                </Label>
                <button
                  id="current_time_enable"
                  type="button"
                  role="switch"
                  aria-checked={form.currentTimeEnable}
                  onClick={() => set('currentTimeEnable', !form.currentTimeEnable)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.currentTimeEnable ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.currentTimeEnable ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                每次模型调用时在消息末尾追加当前时间（时区默认 Asia/Shanghai），存量 Agent
                无此字段时不开启。
              </p>
            </div>
          </SectionCard>

          <SectionCard id="compression" title="压缩配置" subtitle="配置上下文压缩以优化长对话性能">
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <Label htmlFor="compression_enabled" className="text-sm font-medium">
                  启用压缩
                </Label>
                <button
                  id="compression_enabled"
                  type="button"
                  role="switch"
                  aria-checked={form.compression.enabled}
                  onClick={() =>
                    set('compression', {
                      ...form.compression,
                      enabled: !form.compression.enabled,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    form.compression.enabled ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                      form.compression.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {form.compression.enabled && (
                <p className="text-xs text-muted-foreground">
                  启用 summarization 与 summarization tool
                  中间件：按主模型上下文自动摘要，并额外提供工具 compact_conversation
                  供手动压缩；无需配置压缩模型与阈值。保存模板后请同步/重启应用实例方可生效。
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="faultTolerance"
            title="容错配置"
            subtitle="配置任务级 / 步骤级重试、超时与模型降级切换"
          >
            <FaultToleranceFields
              value={form.faultTolerance}
              onChange={(v) => set('faultTolerance', v)}
              groupedModels={credentialPicker && form.api_key_ref ? groupedModels : undefined}
              showConfigurable
            />
          </SectionCard>

          <SectionCard
            id="toolPermissions"
            title="工具权限"
            subtitle="配置 HITL 审批策略：deny 规则优先 → 匹配则直接拒绝；allow 规则 → 匹配则自动通过审批；无匹配则走下方默认行为"
          >
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium">默认行为</Label>
                <p className="text-xs text-muted-foreground">
                  无 allow/deny 规则命中时的处理方式，对应 HITL middleware 的
                  default_action。未选择时：有规则则按「审批」，无规则则不启用 HITL。
                </p>
                <Select
                  value={
                    form.toolPermissions.defaultAction === ToolPermissionDefaultAction.UNSPECIFIED
                      ? undefined
                      : String(form.toolPermissions.defaultAction)
                  }
                  onValueChange={(v) =>
                    set('toolPermissions', {
                      ...form.toolPermissions,
                      defaultAction: Number(v),
                    })
                  }
                >
                  <SelectTrigger className="h-10 w-full max-w-sm">
                    <SelectValue placeholder="审批（有规则时的默认）">
                      {form.toolPermissions.defaultAction === ToolPermissionDefaultAction.DENY
                        ? '拒绝'
                        : form.toolPermissions.defaultAction ===
                            ToolPermissionDefaultAction.AUTO_APPROVE
                          ? '自动批准'
                          : '审批'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(ToolPermissionDefaultAction.DENY)}>拒绝</SelectItem>
                    <SelectItem value={String(ToolPermissionDefaultAction.REQUIRE_HUMAN)}>
                      审批
                    </SelectItem>
                    <SelectItem value={String(ToolPermissionDefaultAction.AUTO_APPROVE)}>
                      自动批准
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* HITL 审批描述生成 */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label htmlFor="hitl_enable_desc" className="text-sm font-medium">
                    生成审批描述
                  </Label>
                  <button
                    id="hitl_enable_desc"
                    type="button"
                    role="switch"
                    aria-checked={form.toolPermissions.enableDesc}
                    onClick={() =>
                      set('toolPermissions', {
                        ...form.toolPermissions,
                        enableDesc: !form.toolPermissions.enableDesc,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      form.toolPermissions.enableDesc ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                        form.toolPermissions.enableDesc ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  开启后，触发人工审批时调用 LLM 生成一句中文意图描述，便于审批人快速理解操作内容。
                </p>
                {form.toolPermissions.enableDesc && (
                  <div className="space-y-1.5 max-w-sm">
                    <Label htmlFor="hitl_desc_model" className="text-xs text-muted-foreground">
                      描述生成模型
                    </Label>
                    <Input
                      id="hitl_desc_model"
                      placeholder="留空使用默认模型（deepseek-v4-flash）"
                      value={form.toolPermissions.descModel}
                      onChange={(e) =>
                        set('toolPermissions', {
                          ...form.toolPermissions,
                          descModel: e.target.value,
                        })
                      }
                      className="h-9 font-mono text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Deny rules */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-destructive">拒绝规则 (deny)</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      set('toolPermissions', {
                        ...form.toolPermissions,
                        deny: [
                          ...form.toolPermissions.deny,
                          { toolName: '', argument: '', pattern: '' },
                        ],
                      })
                    }
                  >
                    <PlusIcon className="h-3.5 w-3.5 mr-1" />
                    添加拒绝规则
                  </Button>
                </div>
                {form.toolPermissions.deny.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                    无拒绝规则
                  </div>
                )}
                {form.toolPermissions.deny.map((rule, i) => (
                  <div
                    key={`deny-${i}`}
                    className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">工具名称</Label>
                        <Input
                          placeholder="exec、file_editor 或 * (所有工具)"
                          value={rule.toolName}
                          onChange={(e) => {
                            const next = [...form.toolPermissions.deny];
                            next[i] = { ...next[i], toolName: e.target.value };
                            set('toolPermissions', { ...form.toolPermissions, deny: next });
                          }}
                          className="h-9 font-mono text-xs"
                        />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">参数名</Label>
                        <Input
                          placeholder="command、path 等（留空表示不按参数过滤）"
                          value={rule.argument}
                          onChange={(e) => {
                            const next = [...form.toolPermissions.deny];
                            next[i] = { ...next[i], argument: e.target.value };
                            set('toolPermissions', { ...form.toolPermissions, deny: next });
                          }}
                          className="h-9 font-mono text-xs"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          set('toolPermissions', {
                            ...form.toolPermissions,
                            deny: form.toolPermissions.deny.filter((_, j) => j !== i),
                          })
                        }
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">匹配正则 (RE2 语法)</Label>
                      <Input
                        placeholder="如 ^rm.* （留空表示无条件拒绝该工具）"
                        value={rule.pattern}
                        onChange={(e) => {
                          const next = [...form.toolPermissions.deny];
                          next[i] = { ...next[i], pattern: e.target.value };
                          set('toolPermissions', { ...form.toolPermissions, deny: next });
                        }}
                        className="h-9 font-mono text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Allow rules */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-green-600">允许规则 (allow)</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      set('toolPermissions', {
                        ...form.toolPermissions,
                        allow: [
                          ...form.toolPermissions.allow,
                          { toolName: '', argument: '', pattern: '' },
                        ],
                      })
                    }
                  >
                    <PlusIcon className="h-3.5 w-3.5 mr-1" />
                    添加允许规则
                  </Button>
                </div>
                {form.toolPermissions.allow.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
                    无允许规则，所有工具调用需人工审批
                  </div>
                )}
                {form.toolPermissions.allow.map((rule, i) => (
                  <div
                    key={`allow-${i}`}
                    className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3 dark:border-green-800 dark:bg-green-950/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">工具名称</Label>
                        <Input
                          placeholder="exec、file_editor 或 * (所有工具)"
                          value={rule.toolName}
                          onChange={(e) => {
                            const next = [...form.toolPermissions.allow];
                            next[i] = { ...next[i], toolName: e.target.value };
                            set('toolPermissions', { ...form.toolPermissions, allow: next });
                          }}
                          className="h-9 font-mono text-xs"
                        />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">参数名</Label>
                        <Input
                          placeholder="command、path 等（留空表示不按参数过滤）"
                          value={rule.argument}
                          onChange={(e) => {
                            const next = [...form.toolPermissions.allow];
                            next[i] = { ...next[i], argument: e.target.value };
                            set('toolPermissions', { ...form.toolPermissions, allow: next });
                          }}
                          className="h-9 font-mono text-xs"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          set('toolPermissions', {
                            ...form.toolPermissions,
                            allow: form.toolPermissions.allow.filter((_, j) => j !== i),
                          })
                        }
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">匹配正则 (RE2 语法)</Label>
                      <Input
                        placeholder="如 ^ls.* 或 ^cat |^grep （留空表示无条件允许该工具）"
                        value={rule.pattern}
                        onChange={(e) => {
                          const next = [...form.toolPermissions.allow];
                          next[i] = { ...next[i], pattern: e.target.value };
                          set('toolPermissions', { ...form.toolPermissions, allow: next });
                        }}
                        className="h-9 font-mono text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard id="channel" title="渠道配置" subtitle="选择需要支持的接入渠道及绑定模式">
            <div className="space-y-4">
              {form.channels.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">
                    绑定模式 <span className="text-destructive">*</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {BINDING_MODE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          if (form.channelBindingMode !== opt.value) {
                            set('channelBindingMode', opt.value);
                            form.channels.forEach((_, idx) => {
                              updateChannel(idx, 'bindingMode', opt.value);
                              updateChannel(idx, 'channelParamValues', {});
                            });
                          }
                        }}
                        className={`rounded-lg border p-3 text-left transition-all ${
                          form.channelBindingMode === opt.value
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : 'border-border hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              form.channelBindingMode === opt.value
                                ? 'bg-primary'
                                : 'bg-muted-foreground/40'
                            }`}
                          />
                          <span className="text-sm font-medium">{opt.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground ml-4">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {form.channels.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 rounded-lg border-2 border-dashed border-border/60 bg-muted/20">
                  <IconPlug className="w-10 h-10 text-muted-foreground/50 mb-3" />
                  <span className="text-sm text-muted-foreground mb-1">暂无渠道配置</span>
                  <span className="text-xs text-muted-foreground/70">点击下方按钮添加渠道</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.channels.map((channel, channelIndex) => {
                    const paramDefs = CHANNEL_PARAM_DEFINITIONS[channel.channelType] ?? [];
                    return (
                      <div
                        key={channelIndex}
                        className="border rounded-lg p-4 space-y-4 bg-muted/20"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">
                            渠道 {channelIndex + 1} — {channelTypeLabel(channel.channelType)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeChannel(channelIndex)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-sm font-medium">
                              渠道类型 <span className="text-destructive">*</span>
                            </Label>
                            <select
                              value={channel.channelType}
                              onChange={(e) =>
                                updateChannel(
                                  channelIndex,
                                  'channelType',
                                  Number(e.target.value) as ChannelType
                                )
                              }
                              className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              {CHANNEL_TYPE_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            {duplicateChannelTypes.has(channel.channelType) && (
                              <p className="text-xs text-destructive">
                                渠道类型重复，请选择不同渠道
                              </p>
                            )}
                          </div>
                        </div>
                        {form.channelBindingMode === ChannelAccountBindingMode.TEMPLATE &&
                          paramDefs.length > 0 && (
                            <div className="pt-3 border-t border-border/40 space-y-3">
                              <span className="text-sm font-medium">渠道参数</span>
                              {paramDefs.map((def) => (
                                <ChannelParamInput
                                  key={def.name}
                                  def={def}
                                  value={channel.channelParamValues[def.name] ?? def.defaultValue}
                                  onChange={(val) => {
                                    const updated = {
                                      ...channel.channelParamValues,
                                      [def.name]: val,
                                    };
                                    updateChannel(channelIndex, 'channelParamValues', updated);
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        {form.channelBindingMode === ChannelAccountBindingMode.INSTANCE && (
                          <p className="text-xs text-muted-foreground pt-2 border-t border-border/40">
                            实例绑定模式下，渠道参数将在创建实例时由用户填写
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {hasDuplicateChannels && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  检测到重复渠道：同一个智能体模版中每种渠道类型只能添加一次。
                </div>
              )}
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={addChannel}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg border-2 border-dashed border-primary/40 text-sm font-medium text-primary hover:bg-primary/5 hover:border-primary/60 transition-all"
                >
                  <PlusIcon className="h-4 w-4" />
                  添加渠道
                </button>
              </div>
            </div>
          </SectionCard>

          <SectionCard id="plugins" title="插件配置" subtitle="为 Agent 添加插件以扩展功能">
            <div className="space-y-4">
              {form.plugins.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 rounded-lg border-2 border-dashed border-border/60 bg-muted/20">
                  <IconPlugin className="w-10 h-10 text-muted-foreground/50 mb-3" />
                  <span className="text-sm text-muted-foreground mb-1">暂无插件配置</span>
                  <span className="text-xs text-muted-foreground/70">点击下方按钮添加插件</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.plugins.map((plugin, pluginIndex) => (
                    <div
                      key={pluginIndex}
                      className="rounded-xl border bg-card shadow-sm overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-5 py-3 border-b border-border/40 bg-muted/20">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                            {pluginIndex + 1}
                          </span>
                          <span className="text-sm font-medium">插件 {pluginIndex + 1}</span>
                          {plugin.selectedItemData?.name && (
                            <span className="text-xs text-muted-foreground">
                              · {plugin.selectedItemData.name}
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removePlugin(pluginIndex)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="p-5 space-y-4">
                        <div className="flex gap-4">
                          <div className="flex items-center gap-3">
                            <Label className="text-sm font-medium shrink-0 w-20">类型</Label>
                            <select
                              value={`${plugin.plugin_type}:${plugin.plugin_sub_type}`}
                              onChange={(e) => {
                                const [type, subType] = e.target.value.split(':');
                                updatePlugin(pluginIndex, 'plugin_type', type);
                                updatePlugin(pluginIndex, 'plugin_sub_type', subType);
                                updatePlugin(pluginIndex, 'plugin_id', '');
                                updatePlugin(
                                  pluginIndex,
                                  'plugin_params',
                                  subType === PLUGIN_SUB_TYPE_USER_TOOLKIT
                                    ? [
                                        {
                                          type: 'string',
                                          name: 'config',
                                          target: '',
                                          value: '',
                                          valueType: 'value',
                                          configurable: true,
                                        },
                                      ]
                                    : []
                                );
                                updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                if (type === PLUGIN_TYPE_MEMORY) {
                                  updatePlugin(pluginIndex, 'memoryMode', 'mem0');
                                  updatePlugin(
                                    pluginIndex,
                                    'memoryInteractionMode',
                                    'AUTO_EXTRACT_AND_TOOL'
                                  );
                                }
                                form.plugins.forEach((_, i) =>
                                  updatePlugin(i, 'isMicroApp', false)
                                );
                              }}
                              className="h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              {pluginTypeOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {plugin.plugin_type === PLUGIN_TYPE_MEMORY && (
                          <div className="flex items-center gap-3">
                            <Label className="text-sm font-medium shrink-0 w-20">记忆方案</Label>
                            <select
                              value={plugin.memoryMode ?? 'mem0'}
                              onChange={(event) => {
                                const mode = event.target.value as 'mem0' | 'infini_memory';
                                updatePlugin(pluginIndex, 'memoryMode', mode);
                                updatePlugin(
                                  pluginIndex,
                                  'plugin_sub_type',
                                  PLUGIN_SUB_TYPE_INSTANCE
                                );
                                updatePlugin(pluginIndex, 'plugin_id', '');
                                updatePlugin(pluginIndex, 'plugin_params', []);
                                updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                updatePlugin(
                                  pluginIndex,
                                  'memoryInteractionMode',
                                  'AUTO_EXTRACT_AND_TOOL'
                                );
                              }}
                              className="h-10 w-60 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="mem0">mem0</option>
                              {showInfiniMemory && (
                                <option
                                  value="infini_memory"
                                  disabled={infiniMemoryScopeQuery.data?.available === false}
                                >
                                  infini-memory
                                </option>
                              )}
                            </select>
                            {plugin.memoryMode === 'infini_memory' &&
                              infiniMemoryScopeQuery.data?.available === false && (
                                <span className="text-xs text-amber-600">
                                  {infiniMemoryScopeQuery.data.unavailableReason}
                                </span>
                              )}
                          </div>
                        )}

                        {plugin.plugin_type === PLUGIN_TYPE_MEMORY &&
                          plugin.memoryMode === 'infini_memory' && (
                            <div className="space-y-3 rounded-md border border-border/60 p-3">
                              <div className="flex items-center gap-3">
                                <Label className="text-sm font-medium shrink-0 w-20">
                                  Store ID
                                </Label>
                                <select
                                  value={plugin.plugin_id}
                                  onChange={(event) =>
                                    updatePlugin(pluginIndex, 'plugin_id', event.target.value)
                                  }
                                  className="h-10 w-80 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  disabled={infiniMemoryStoresQuery.isLoading}
                                >
                                  <option value="">选择已配置的记忆库</option>
                                  {infiniMemoryStoresQuery.data?.map((value) => (
                                    <option key={value} value={value}>
                                      {value}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_INSTANCE &&
                          !(
                            plugin.plugin_type === PLUGIN_TYPE_MEMORY &&
                            plugin.memoryMode === 'infini_memory'
                          ) && (
                            <div className="flex items-center gap-3">
                              <Label className="text-sm font-medium shrink-0 w-20">选择实例</Label>
                              <select
                                value={plugin.plugin_id}
                                onChange={(e) => {
                                  updatePlugin(pluginIndex, 'plugin_id', e.target.value);
                                  if (e.target.value) {
                                    fetchPluginInfo(
                                      pluginIndex,
                                      plugin.plugin_type,
                                      e.target.value,
                                      PLUGIN_SUB_TYPE_INSTANCE
                                    );
                                  } else {
                                    updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                  }
                                }}
                                className="h-10 w-80 rounded-md border border-input bg-background px-3 py-2 text-sm"
                              >
                                <option value="">选择实例...</option>
                                {plugin.plugin_type === PLUGIN_TYPE_SANDBOX &&
                                  sandboxList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_SKILLGROUP &&
                                  skillGroupsList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_RAG &&
                                  ragsList?.data?.data?.map((s: { id: string; name: string }) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name} ({s.id})
                                    </option>
                                  ))}
                                {plugin.plugin_type === PLUGIN_TYPE_MEMORY &&
                                  memoryList?.data?.data?.map((s: { id: string; name: string }) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name} ({s.id})
                                    </option>
                                  ))}
                                {plugin.plugin_type === PLUGIN_TYPE_MCP &&
                                  mcpList?.data?.data?.map((s: { id: string; name: string }) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name} ({s.id})
                                    </option>
                                  ))}
                              </select>
                            </div>
                          )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_CLASS &&
                          !(
                            plugin.plugin_type === PLUGIN_TYPE_MEMORY &&
                            plugin.memoryMode === 'infini_memory'
                          ) && (
                            <div className="flex items-center gap-3">
                              <Label className="text-sm font-medium shrink-0 w-20">选择模版</Label>
                              <select
                                value={plugin.plugin_id}
                                onChange={(e) => {
                                  updatePlugin(pluginIndex, 'plugin_id', e.target.value);
                                  if (e.target.value) {
                                    fetchPluginInfo(
                                      pluginIndex,
                                      plugin.plugin_type,
                                      e.target.value,
                                      PLUGIN_SUB_TYPE_CLASS
                                    );
                                  } else {
                                    updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                  }
                                }}
                                className="h-10 w-80 rounded-md border border-input bg-background px-3 py-2 text-sm"
                              >
                                <option value="">选择模版...</option>
                                {plugin.plugin_type === PLUGIN_TYPE_SANDBOX &&
                                  sandboxClassesList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_SKILLGROUP &&
                                  skillGroupClassesList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_RAG &&
                                  ragClassesList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_MEMORY &&
                                  memoryClassesList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                                {plugin.plugin_type === PLUGIN_TYPE_MCP &&
                                  mcpClassesList?.data?.data?.map(
                                    (s: { id: string; name: string }) => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.id})
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>
                          )}

                        {plugin.plugin_type === PLUGIN_TYPE_MEMORY && (
                          <div className="flex items-center gap-3">
                            <Label className="text-sm font-medium shrink-0 w-20">交互模式</Label>
                            <select
                              value={plugin.memoryInteractionMode || 'AUTO_EXTRACT_AND_TOOL'}
                              onChange={(e) =>
                                updatePlugin(pluginIndex, 'memoryInteractionMode', e.target.value)
                              }
                              className="h-10 w-60 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="AUTO_EXTRACT_AND_TOOL">
                                AUTO_EXTRACT_AND_TOOL - 自动提取记忆、通过工具调用检索记忆
                              </option>
                              <option value="TOOL">TOOL - 仅通过工具调用提取记忆和检索记忆</option>
                            </select>
                          </div>
                        )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_TOOLKIT && (
                          <div className="flex items-center gap-3">
                            <Label className="text-sm font-medium shrink-0 w-20">选择工具包</Label>
                            <select
                              value={plugin.plugin_id}
                              onChange={(e) => {
                                updatePlugin(pluginIndex, 'plugin_id', e.target.value);
                                if (e.target.value) {
                                  const tk = toolkitList?.data?.data?.find(
                                    (t) => t.name === e.target.value
                                  );
                                  updatePlugin(pluginIndex, 'selectedItemData', {
                                    name: tk?.name || '',
                                    description: tk?.description || '',
                                    type: '',
                                    provider: '',
                                  });
                                } else {
                                  updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                }
                              }}
                              className="h-10 w-80 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="">选择工具包...</option>
                              {toolkitList?.data?.data?.map(
                                (t: { name: string; description: string }) => (
                                  <option key={t.name} value={t.name}>
                                    {t.name} - {t.description}
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_USER_TOOLKIT &&
                          plugin.plugin_params.length > 0 && (
                            <div className="space-y-3">
                              <JsonInput
                                value={plugin.plugin_params[0]?.value || ''}
                                onChange={(val) => updatePluginParam(pluginIndex, 0, 'value', val)}
                              />
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={plugin.plugin_params[0]?.configurable ?? true}
                                  onChange={(e) => {
                                    const updated = [...plugin.plugin_params];
                                    updated[0] = {
                                      ...updated[0],
                                      configurable: e.target.checked,
                                    };
                                    updatePlugin(pluginIndex, 'plugin_params', updated);
                                  }}
                                  className="rounded border-input"
                                />
                                <span className="text-sm text-muted-foreground">
                                  创建实例时可配置
                                </span>
                              </label>
                            </div>
                          )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_APPLICATION && (
                          <div className="flex items-center gap-3">
                            <Label className="text-sm font-medium shrink-0 w-20">
                              选择智能体实例
                            </Label>
                            <select
                              value={plugin.plugin_id}
                              onChange={(e) => {
                                updatePlugin(pluginIndex, 'plugin_id', e.target.value);
                                if (e.target.value) {
                                  fetchPluginInfo(
                                    pluginIndex,
                                    plugin.plugin_type,
                                    e.target.value,
                                    PLUGIN_SUB_TYPE_APPLICATION
                                  );
                                } else {
                                  updatePlugin(pluginIndex, 'selectedItemData', undefined);
                                }
                              }}
                              className="h-10 w-80 rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                              <option value="">选择智能体实例...</option>
                              {applicationList?.data?.data?.map(
                                (a: { id: string; name: string; description: string }) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name} ({a.id})
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        )}

                        {plugin.plugin_sub_type === PLUGIN_SUB_TYPE_CLASS && (
                          <>
                            {plugin.plugin_params.length > 0 && (
                              <div className="pt-2 border-t border-border/40">
                                <div className="flex items-center gap-2 mb-3">
                                  <span className="text-sm font-medium">参数配置</span>
                                  <span className="text-xs text-muted-foreground">
                                    {plugin.plugin_params.length} 个参数
                                  </span>
                                </div>
                                <div className="space-y-3">
                                  {(() => {
                                    const duplicates = getDuplicateParamNames();
                                    return plugin.plugin_params.map((param, paramIndex) => (
                                      <div
                                        key={paramIndex}
                                        className="rounded-lg border border-border/60 bg-muted/5 p-3"
                                      >
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="flex items-center justify-center w-5 h-5 rounded bg-primary/10 text-primary text-[11px] font-semibold shrink-0">
                                            {paramIndex + 1}
                                          </span>
                                          <span className="text-xs font-mono text-foreground truncate">
                                            {param.name || '未命名'}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <Label className="text-[11px] text-muted-foreground mb-1 block">
                                              参数名
                                            </Label>
                                            <Input
                                              placeholder="参数名称"
                                              value={param.name}
                                              onChange={(e) =>
                                                updatePluginParam(
                                                  pluginIndex,
                                                  paramIndex,
                                                  'name',
                                                  e.target.value
                                                )
                                              }
                                              className={`h-8 text-xs font-mono ${duplicates.has(param.name) && param.name ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                            />
                                            {duplicates.has(param.name) && param.name && (
                                              <p className="text-[11px] text-destructive mt-1">
                                                参数名已重复
                                              </p>
                                            )}
                                          </div>
                                          <div>
                                            <Label className="text-[11px] text-muted-foreground mb-1 block">
                                              参数值
                                            </Label>
                                            <Input
                                              placeholder="默认值"
                                              value={param.value}
                                              onChange={(e) =>
                                                updatePluginParam(
                                                  pluginIndex,
                                                  paramIndex,
                                                  'value',
                                                  e.target.value
                                                )
                                              }
                                              className="h-8 text-xs font-mono"
                                            />
                                          </div>
                                        </div>
                                        <div className="mt-2">
                                          <Label className="text-[11px] text-muted-foreground mb-1 block">
                                            目标路径
                                          </Label>
                                          <div className="flex items-center px-2 py-1 rounded bg-muted/50 text-[11px] font-mono text-muted-foreground">
                                            {param.target || '—'}
                                          </div>
                                        </div>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={() =>
                    addPlugin({
                      plugin_type: firstAvailablePluginType.pluginType,
                      plugin_sub_type: firstAvailablePluginType.pluginSubType,
                    })
                  }
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg border-2 border-dashed border-primary/40 text-sm font-medium text-primary hover:bg-primary/5 hover:border-primary/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <PlusIcon className="h-4 w-4" />
                  添加插件
                </button>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="ac-form-bottom-pad" />
      </div>

      <footer className="ac-submit-bar">
        <div className="ac-submit-bar-info">
          <span className={`ac-submit-status ${statusOk ? 'is-ok' : 'is-warn'}`}>
            {statusOk ? '已满足最小创建条件' : '还需：模板名称 + 模型凭证 + 模型'}
          </span>
        </div>
        <div className="ac-submit-bar-actions">
          <Button variant="outline" onClick={() => navigate(backTo)}>
            取消
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isPending || agentLoading || !canSubmit || hasDuplicateChannels}
            className="gap-2"
          >
            {isPending ? (
              <>
                <LoaderIcon className="h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
}
